import React, { useEffect, useRef, useState } from 'react';
import { Users, Plus, RefreshCw, Copy, CheckCircle2, X, ShieldOff, QrCode, Download } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';

/**
 * Admin card for managing lite invite codes.
 * Each invite produces a shareable link of the form
 *   https://<your-domain>/lite/<code>
 * Users who sign up via that link get app_mode='lite'.
 */
const AdminOrgInvites = () => {
  // Combined state so the bootstrap effect only calls a single setter
  const [data, setData] = useState({ invites: [], loading: true });
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [form, setForm] = useState({ code: '', name: '', max_users: '' });
  const [copiedCode, setCopiedCode] = useState('');
  const [qrInvite, setQrInvite] = useState(null);  // { code, name } for the QR modal
  const qrSvgRef = useRef(null);

  const token = () => localStorage.getItem('session_token');

  const fetchInvites = async () => {
    let invites = [];
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/orgs`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      if (res.ok) {
        const body = await res.json();
        invites = body.invites || [];
      }
    } catch (e) {
      console.error('Failed to fetch invites', e);
    }
    setData({ invites, loading: false });
  };

  useEffect(() => {
    fetchInvites();
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!form.code || !form.name) {
      setError('Code and name are both required.');
      setTimeout(() => setError(''), 4000);
      return;
    }
    setCreating(true);
    try {
      const body = {
        code: form.code.toLowerCase().trim(),
        name: form.name.trim(),
      };
      if (form.max_users) body.max_users = parseInt(form.max_users, 10);
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/orgs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        setForm({ code: '', name: '', max_users: '' });
        setSuccess('Invite created — share the link below.');
        setTimeout(() => setSuccess(''), 4000);
        await fetchInvites();
      } else {
        const data = await res.json();
        setError(data.detail || 'Could not create invite');
        setTimeout(() => setError(''), 4000);
      }
    } catch (err) {
      setError('Network error. Please retry.');
      setTimeout(() => setError(''), 4000);
    } finally {
      setCreating(false);
    }
  };

  const handleToggleActive = async (invite) => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/orgs/${invite.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body: JSON.stringify({ is_active: !invite.is_active }),
      });
      if (res.ok) await fetchInvites();
    } catch (e) {
      console.error(e);
    }
  };

  const handleDisable = async (invite) => {
    if (!window.confirm(`Disable invite "${invite.name}"? Existing users keep working — only NEW signups under this code will be blocked.`)) return;
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/orgs/${invite.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token()}` },
      });
      if (res.ok) await fetchInvites();
    } catch (e) {
      console.error(e);
    }
  };

  const inviteUrl = (code) => `${window.location.origin}/lite/${code}`;

  const copyLink = async (code) => {
    try {
      await navigator.clipboard.writeText(inviteUrl(code));
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(''), 1800);
    } catch (e) {
      console.error('clipboard write failed', e);
    }
  };

  // Rasterise the QR SVG to a 1024×1024 PNG and trigger a download. Bigger than
  // the on-screen 224px size so it stays crisp when dropped into slides.
  const downloadQrPng = (code) => {
    const svg = qrSvgRef.current;
    if (!svg) return;
    const xml = new XMLSerializer().serializeToString(svg);
    const svgBlob = new Blob([xml], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(svgBlob);
    const img = new Image();
    img.onload = () => {
      const size = 1024;
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, size, size);
      ctx.drawImage(img, 0, 0, size, size);
      URL.revokeObjectURL(url);
      canvas.toBlob((blob) => {
        if (!blob) return;
        const dl = document.createElement('a');
        dl.href = URL.createObjectURL(blob);
        dl.download = `reflectivemind-invite-${code}.png`;
        document.body.appendChild(dl);
        dl.click();
        document.body.removeChild(dl);
        URL.revokeObjectURL(dl.href);
      }, 'image/png');
    };
    img.src = url;
  };

  return (
    <div
      data-testid="org-invites-card"
      className="bg-white rounded-xl shadow-sm p-6 mb-6 border border-gray-100"
    >
      <div className="flex items-center justify-between mb-1">
        <h2 className="text-xl font-bold text-gray-900 flex items-center">
          <Users className="h-6 w-6 mr-2 text-brand-accent" />
          Lite invite codes
        </h2>
        <button
          onClick={fetchInvites}
          className="text-xs text-brand-accent hover:text-brand-accent-hover transition-colors flex items-center gap-1"
          data-testid="refresh-invites-btn"
        >
          <RefreshCw className="h-3.5 w-3.5" /> Refresh
        </button>
      </div>
      <p className="text-sm text-gray-600 mb-5">
        Create a code, then share the URL (or QR) at trainings, intros or presentations. Any new sign-up via that link gets the focused <strong>Lite</strong> experience (reflections + smart tagging + sentiment + on-demand weekly AI summary). Existing users are never affected.
      </p>

      {/* Create form */}
      <form onSubmit={handleCreate} className="bg-brand-accent/5 border border-brand-accent/20 rounded-lg p-4 mb-6" data-testid="create-invite-form">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
          <div className="md:col-span-4">
            <label className="block text-xs font-semibold text-gray-700 mb-1">Code (URL slug)</label>
            <input
              type="text"
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value })}
              placeholder="e.g. reflectloop"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-accent focus:border-transparent"
              data-testid="invite-code-input"
            />
          </div>
          <div className="md:col-span-5">
            <label className="block text-xs font-semibold text-gray-700 mb-1">Internal name</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. ReflectLoop training pilot"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-accent focus:border-transparent"
              data-testid="invite-name-input"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs font-semibold text-gray-700 mb-1">Max users</label>
            <input
              type="number"
              min="1"
              value={form.max_users}
              onChange={(e) => setForm({ ...form, max_users: e.target.value })}
              placeholder="∞"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-accent focus:border-transparent"
              data-testid="invite-max-users-input"
            />
          </div>
          <div className="md:col-span-1 flex items-end">
            <button
              type="submit"
              disabled={creating}
              className="w-full bg-brand-accent hover:bg-brand-accent-hover text-white py-2 px-3 rounded-lg text-sm font-semibold disabled:opacity-50 flex items-center justify-center gap-1"
              data-testid="create-invite-btn"
            >
              {creating ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            </button>
          </div>
        </div>
        {error && <div className="mt-3 text-xs text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</div>}
        {success && <div className="mt-3 text-xs text-green-700 bg-green-50 border border-green-200 rounded px-3 py-2">{success}</div>}
      </form>

      {/* List */}
      {data.loading ? (
        <div className="py-10 text-center text-sm text-gray-500">
          <RefreshCw className="h-5 w-5 animate-spin mx-auto mb-2" /> Loading invites…
        </div>
      ) : data.invites.length === 0 ? (
        <div className="py-10 text-center text-sm text-gray-500" data-testid="invites-empty">
          No invite codes yet. Create one above to invite a partner org.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 text-left text-xs font-semibold text-gray-500 uppercase">
                <th className="py-2 pr-3">Status</th>
                <th className="py-2 pr-3">Code · Name</th>
                <th className="py-2 pr-3">Usage</th>
                <th className="py-2 pr-3">Link</th>
                <th className="py-2 pr-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {data.invites.map((inv) => {
                const url = inviteUrl(inv.code);
                const isFull = inv.max_users && inv.used_count >= inv.max_users;
                return (
                  <tr key={inv.id} className="hover:bg-gray-50" data-testid={`invite-row-${inv.code}`}>
                    <td className="py-3 pr-3">
                      {inv.is_active && !isFull ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-50 text-green-700 text-xs font-medium border border-green-200">
                          <CheckCircle2 className="h-3 w-3" /> Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-50 text-gray-600 text-xs font-medium border border-gray-200">
                          <ShieldOff className="h-3 w-3" /> {isFull ? 'Full' : 'Disabled'}
                        </span>
                      )}
                    </td>
                    <td className="py-3 pr-3">
                      <div className="font-mono font-semibold text-brand-ink">/lite/{inv.code}</div>
                      <div className="text-xs text-gray-500">{inv.name}</div>
                    </td>
                    <td className="py-3 pr-3 text-gray-700 whitespace-nowrap">
                      <span className="font-semibold">{inv.used_count}</span>
                      {inv.max_users ? <span className="text-gray-400"> / {inv.max_users}</span> : <span className="text-gray-400"> / ∞</span>}
                    </td>
                    <td className="py-3 pr-3 whitespace-nowrap">
                      <button
                        onClick={() => copyLink(inv.code)}
                        className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded border border-gray-200 text-gray-700 hover:bg-gray-50"
                        title={url}
                        data-testid={`copy-link-${inv.code}`}
                      >
                        {copiedCode === inv.code ? (
                          <><CheckCircle2 className="h-3.5 w-3.5 text-green-600" /> Copied</>
                        ) : (
                          <><Copy className="h-3.5 w-3.5" /> Copy link</>
                        )}
                      </button>
                    </td>
                    <td className="py-3 pl-3 text-right whitespace-nowrap w-px">
                      <div className="inline-flex items-center gap-2">
                        <button
                          onClick={() => setQrInvite({ code: inv.code, name: inv.name })}
                          className="text-xs px-2.5 py-1 rounded border border-brand-accent/30 text-brand-accent hover:bg-brand-accent/5 inline-flex items-center gap-1"
                          data-testid={`qr-invite-${inv.code}`}
                          title="Show QR code"
                        >
                          <QrCode className="h-3.5 w-3.5" />
                          QR
                        </button>
                        <button
                          onClick={() => handleToggleActive(inv)}
                          className={`text-xs px-2.5 py-1 rounded border transition-colors ${
                            inv.is_active
                              ? 'border-gray-200 text-gray-700 hover:bg-gray-50'
                              : 'border-brand-accent/30 text-brand-accent hover:bg-brand-accent/5'
                          }`}
                          data-testid={`toggle-invite-${inv.code}`}
                        >
                          {inv.is_active ? 'Pause' : 'Resume'}
                        </button>
                        <button
                          onClick={() => handleDisable(inv)}
                          className="text-xs px-2.5 py-1 rounded border border-red-200 text-red-700 hover:bg-red-50"
                          data-testid={`disable-invite-${inv.code}`}
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* QR code modal — invite link as a scannable square. Used for slide decks,
          printed handouts and on-screen training intros. */}
      {qrInvite && (
        <div
          className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setQrInvite(null)}
          data-testid="qr-modal-backdrop"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 border border-gray-100"
            data-testid="qr-modal"
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="font-bold text-gray-900 text-lg">Invite QR code</h3>
                <p className="text-xs text-gray-500 mt-0.5">{qrInvite.name}</p>
              </div>
              <button
                onClick={() => setQrInvite(null)}
                className="text-gray-400 hover:text-gray-700"
                data-testid="qr-modal-close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="flex justify-center bg-white border border-gray-200 rounded-xl p-4 mb-4">
              <QRCodeSVG
                ref={qrSvgRef}
                value={inviteUrl(qrInvite.code)}
                size={224}
                level="M"
                marginSize={2}
                fgColor="#0A1B3D"
                bgColor="#FFFFFF"
                data-testid="qr-svg"
              />
            </div>

            <div className="text-center text-xs font-mono text-gray-600 break-all mb-4" data-testid="qr-url">
              {inviteUrl(qrInvite.code)}
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => copyLink(qrInvite.code)}
                className="flex-1 inline-flex items-center justify-center gap-1.5 text-sm px-3 py-2 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50"
                data-testid="qr-copy-link"
              >
                {copiedCode === qrInvite.code ? (
                  <><CheckCircle2 className="h-4 w-4 text-green-600" /> Copied!</>
                ) : (
                  <><Copy className="h-4 w-4" /> Copy link</>
                )}
              </button>
              <button
                onClick={() => downloadQrPng(qrInvite.code)}
                className="flex-1 inline-flex items-center justify-center gap-1.5 text-sm px-3 py-2 rounded-lg bg-brand-accent hover:bg-brand-accent-hover text-white font-semibold"
                data-testid="qr-download-png"
              >
                <Download className="h-4 w-4" /> Download PNG
              </button>
            </div>

            <p className="mt-4 text-[11px] text-gray-500 text-center">
              Scan with any phone camera. Drop it into slides or print it for hand-outs.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminOrgInvites;
